import React, { useEffect, useState, useCallback } from "react";
import { ImagePlus, Trash2, ArrowUp, ArrowDown, Eye, EyeOff, Loader2, Save, X } from "lucide-react";
import { toast } from "sonner";
import api, { API, formatApiError } from "../../lib/api";
import { loadUploadWidget } from "../../lib/cloudinary";

const MAX_SLIDES = 12;

export default function AdminGallery() {
  const [items, setItems] = useState([]);
  const [config, setConfig] = useState(null);
  const [drafts, setDrafts] = useState([]); // freshly uploaded, not yet saved
  const [savingId, setSavingId] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);

  const load = useCallback(() => {
    api.get("/admin/gallery").then(({ data }) => setItems(data)).catch(() => {});
  }, []);

  useEffect(() => {
    load();
    api.get("/admin/cloudinary/config").then(({ data }) => setConfig(data)).catch(() => {});
  }, [load]);

  const openWidget = async () => {
    if (!config?.configured) return toast.error("Cloudinary is not configured.");
    try {
      const cloudinary = await loadUploadWidget();
      const widget = cloudinary.createUploadWidget(
        {
          cloudName: config.cloud_name,
          apiKey: config.api_key,
          uploadSignature: async (cb, paramsToSign) => {
            try {
              const { data } = await api.post("/admin/cloudinary/signature", { params_to_sign: paramsToSign });
              cb(data.signature);
            } catch (e) {
              toast.error("Could not sign upload");
            }
          },
          folder: config.folder,
          sources: ["local", "camera", "url"],
          multiple: true,
          maxFiles: 6,
          cropping: true,
          croppingAspectRatio: 4 / 3,
          croppingShowDimensions: true,
          showSkipCropButton: false,
          maxImageWidth: 1600,
          clientAllowedFormats: ["jpg", "jpeg", "png", "webp"],
          maxFileSize: 10000000,
          styles: { palette: { link: "#661361", action: "#661361", tabIcon: "#661361" } },
        },
        (error, result) => {
          if (!error && result && result.event === "success") {
            const info = result.info;
            setDrafts((prev) => [...prev, { public_id: info.public_id, url: info.secure_url, event_type: "", guests: "", location: "" }]);
          }
        }
      );
      widget.open();
    } catch (e) {
      toast.error("Could not load the upload widget");
    }
  };

  const saveDraft = async (idx) => {
    const d = drafts[idx];
    if (!d.event_type && !d.location) return toast.error("Add at least an event type or location");
    setSavingId(d.public_id);
    try {
      await api.post("/admin/gallery", {
        public_id: d.public_id,
        event_type: d.event_type,
        guests: d.guests ? parseInt(d.guests) : null,
        location: d.location,
      });
      setDrafts((prev) => prev.filter((_, i) => i !== idx));
      load();
      toast.success("Slide added");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSavingId(null); }
  };

  const patchItem = async (item, changes) => {
    try {
      const { data } = await api.patch(`/admin/gallery/${item.id}`, changes);
      setItems((prev) => prev.map((x) => (x.id === item.id ? data : x)));
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const move = async (index, dir) => {
    const arr = [...items];
    const j = index + dir;
    if (j < 0 || j >= arr.length) return;
    [arr[index], arr[j]] = [arr[j], arr[index]];
    setItems(arr);
    try { await api.post("/admin/gallery/reorder", { ids: arr.map((i) => i.id) }); }
    catch (e) { toast.error("Reorder failed"); load(); }
  };

  const doDelete = async (item) => {
    try {
      await api.delete(`/admin/gallery/${item.id}`);
      setItems((prev) => prev.filter((x) => x.id !== item.id));
      setConfirmDelete(null);
      toast.success("Slide deleted");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  // sort: visible first (by sort_order), hidden last
  const sorted = [...items].sort((a, b) => (a.visible === b.visible ? a.sort_order - b.sort_order : a.visible ? -1 : 1));
  const visibleCount = items.filter((i) => i.visible).length;

  return (
    <div>
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <h1 className="font-serif text-3xl font-bold text-sapore-purple">Event Gallery</h1>
        <button data-testid="add-photos-button" onClick={openWidget} className="inline-flex items-center gap-2 bg-sapore-gold text-sapore-ink font-semibold px-5 py-3 rounded-full hover:bg-sapore-goldHover" style={{ minHeight: 44 }}>
          <ImagePlus size={18} /> Add photos
        </button>
      </div>

      {config && !config.configured && (
        <div className="bg-amber-100 text-amber-800 rounded-xl px-4 py-3 mb-5 text-sm">Cloudinary is not configured — set CLOUDINARY_* env vars to enable uploads.</div>
      )}
      {visibleCount > MAX_SLIDES && (
        <div data-testid="over-limit-warning" className="bg-amber-100 text-amber-800 rounded-xl px-4 py-3 mb-5 text-sm">You have {visibleCount} photos — only the first {MAX_SLIDES} will display on the site.</div>
      )}

      {/* Drafts awaiting captions */}
      {drafts.length > 0 && (
        <div className="mb-8" data-testid="gallery-drafts">
          <h2 className="font-serif text-xl text-sapore-purple mb-3">New — add details &amp; save</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {drafts.map((d, i) => (
              <div key={d.public_id} className="bg-white rounded-xl border-2 border-sapore-gold p-3">
                <img src={d.url} alt="" className="w-full aspect-[4/3] object-cover rounded-lg mb-2" />
                <input data-testid={`draft-event-${i}`} placeholder="Event type (e.g. Corporate breakfast)" value={d.event_type} onChange={(e) => setDrafts((p) => p.map((x, j) => j === i ? { ...x, event_type: e.target.value } : x))} className="w-full rounded-lg border border-sapore-border p-2 text-sm mb-2" />
                <div className="flex gap-2 mb-2">
                  <input data-testid={`draft-guests-${i}`} type="number" placeholder="Guests" value={d.guests} onChange={(e) => setDrafts((p) => p.map((x, j) => j === i ? { ...x, guests: e.target.value } : x))} className="w-24 rounded-lg border border-sapore-border p-2 text-sm" />
                  <input data-testid={`draft-location-${i}`} placeholder="Location" value={d.location} onChange={(e) => setDrafts((p) => p.map((x, j) => j === i ? { ...x, location: e.target.value } : x))} className="flex-1 rounded-lg border border-sapore-border p-2 text-sm" />
                </div>
                <div className="flex gap-2">
                  <button data-testid={`draft-save-${i}`} onClick={() => saveDraft(i)} disabled={savingId === d.public_id} className="flex-1 inline-flex items-center justify-center gap-1.5 bg-sapore-purple text-sapore-cream text-sm font-semibold py-2 rounded-full">
                    {savingId === d.public_id ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />} Save
                  </button>
                  <button onClick={() => setDrafts((p) => p.filter((_, j) => j !== i))} className="px-3 rounded-full border border-sapore-border text-sapore-inkSoft"><X size={16} /></button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Current slides */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {sorted.map((item, index) => (
          <div key={item.id} data-testid={`gallery-item-${item.id}`} className={`bg-white rounded-xl border border-sapore-border p-3 ${item.visible ? "" : "opacity-50"}`}>
            <div className="relative">
              <img src={item.url} alt={item.event_type} className="w-full aspect-[4/3] object-cover rounded-lg" />
              <span className="absolute top-2 left-2 bg-sapore-purple text-sapore-cream text-xs font-bold w-6 h-6 rounded-full flex items-center justify-center">{index + 1}</span>
            </div>
            <p className="font-semibold text-sapore-ink mt-2 text-sm">{item.event_type || "—"}</p>
            <p className="text-xs text-sapore-muted">{item.guests ? `${item.guests} guests` : ""}{item.location ? ` · ${item.location}` : ""}</p>
            <div className="flex items-center gap-1 mt-3">
              <button data-testid={`gallery-up-${item.id}`} onClick={() => move(index, -1)} className="p-2 rounded-lg border border-sapore-border" aria-label="Move up"><ArrowUp size={15} /></button>
              <button data-testid={`gallery-down-${item.id}`} onClick={() => move(index, 1)} className="p-2 rounded-lg border border-sapore-border" aria-label="Move down"><ArrowDown size={15} /></button>
              <button data-testid={`gallery-visible-${item.id}`} onClick={() => patchItem(item, { visible: !item.visible })} className="p-2 rounded-lg border border-sapore-border ml-auto" aria-label="Toggle visibility">{item.visible ? <Eye size={15} /> : <EyeOff size={15} />}</button>
              <button data-testid={`gallery-delete-${item.id}`} onClick={() => setConfirmDelete(item)} className="p-2 rounded-lg border border-sapore-border text-red-600" aria-label="Delete"><Trash2 size={15} /></button>
            </div>
            <details className="mt-2">
              <summary className="text-xs text-sapore-purple cursor-pointer">Edit captions</summary>
              <input defaultValue={item.event_type} onBlur={(e) => patchItem(item, { event_type: e.target.value })} placeholder="Event type" className="w-full rounded-lg border border-sapore-border p-2 text-sm mt-2" />
              <div className="flex gap-2 mt-2">
                <input type="number" defaultValue={item.guests || ""} onBlur={(e) => patchItem(item, { guests: e.target.value ? parseInt(e.target.value) : null })} placeholder="Guests" className="w-24 rounded-lg border border-sapore-border p-2 text-sm" />
                <input defaultValue={item.location} onBlur={(e) => patchItem(item, { location: e.target.value })} placeholder="Location" className="flex-1 rounded-lg border border-sapore-border p-2 text-sm" />
              </div>
            </details>
          </div>
        ))}
      </div>
      {items.length === 0 && drafts.length === 0 && <p className="text-sapore-muted mt-6">No photos yet. Tap “Add photos” to upload your first event.</p>}

      {confirmDelete && (
        <div className="fixed inset-0 z-50 bg-sapore-ink/50 flex items-center justify-center p-4" onClick={() => setConfirmDelete(null)}>
          <div className="bg-white rounded-2xl p-6 max-w-sm w-full" onClick={(e) => e.stopPropagation()} data-testid="delete-confirm">
            <h3 className="font-serif text-xl text-sapore-purple mb-2">Delete this photo?</h3>
            <p className="text-sm text-sapore-inkSoft mb-4">“{confirmDelete.event_type || "Untitled"}{confirmDelete.location ? ` · ${confirmDelete.location}` : ""}” will be permanently removed from Cloudinary. This cannot be undone.</p>
            <div className="flex gap-2">
              <button onClick={() => setConfirmDelete(null)} className="flex-1 py-2.5 rounded-full border border-sapore-border">Cancel</button>
              <button data-testid="confirm-delete-button" onClick={() => doDelete(confirmDelete)} className="flex-1 py-2.5 rounded-full bg-red-600 text-white font-semibold">Delete</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
